import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import {
  Toast, ToastAction, ToastVariant, VoyagerToastService,
} from './voyager-toast.service';

/** Per-variant icon name from the {@code app-icon} sprite. Matches the
 *  reference design's {@code VARIANTS} table. */
const VARIANT_ICONS: Record<ToastVariant, string> = {
  success: 'Check',
  error:   'Warning',
  warning: 'Warning',
  info:    'Info',
  loading: 'Spark',
  offline: 'Wifi',
  neutral: 'Bell',
};

/**
 * Voyager toast host — renders the floating stack of in-flight toasts
 * the {@link VoyagerToastService} maintains.
 *
 * <p>Mount once at the app root (next to {@code <router-outlet>}). The
 * stack class picks one of the four corner docks based on
 * {@code service.position()}. The component is pointer-events: none
 * itself so users can still click through to the page; only the
 * individual toast rows accept clicks.
 */
@Component({
  selector: 'app-voyager-toast-host',
  standalone: true,
  imports: [IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './voyager-toast-host.component.scss',
  template: `
    <div class="vt-stack" [class.br]="position()==='br'" [class.bl]="position()==='bl'"
         [class.tr]="position()==='tr'" [class.tc]="position()==='tc'"
         aria-live="polite" aria-atomic="false">
      @for (t of toasts(); track t.id) {
        <div class="vt" [class]="t.variant"
             [class.vt-enter]="!t.leaving"
             [class.vt-leave]="t.leaving"
             role="status">
          <div class="vt-ic">
            @if (t.variant === 'loading') {
              <span class="vt-spin">
                <app-icon [name]="iconFor(t)" [size]="16"></app-icon>
              </span>
            } @else {
              <app-icon [name]="iconFor(t)" [size]="16"></app-icon>
            }
          </div>
          <div class="vt-body">
            @if (titleFor(t); as title) {
              <div class="vt-title">{{ title }}</div>
            }
            @if (t.message) {
              <div class="vt-msg">{{ t.message }}</div>
            }
            @if (t.actions && t.actions.length) {
              <div class="vt-actions">
                @for (a of t.actions; track $index) {
                  <button type="button" class="vt-act" [class]="a.kind || ''"
                          (click)="invokeAction(a, t.id)">
                    @if (a.icon) {
                      <app-icon [name]="a.icon" [size]="13"></app-icon>
                    }
                    {{ a.label }}
                  </button>
                }
              </div>
            }
          </div>
          @if (t.dismissible !== false) {
            <button type="button" class="vt-x" (click)="service.dismiss(t.id)" aria-label="Dismiss">
              <app-icon name="Close" [size]="14"></app-icon>
            </button>
          }
          @if (t.duration > 0 && !t.leaving) {
            <div class="vt-prog run" [style.--vt-dur]="t.duration + 'ms'"></div>
          }
        </div>
      }
    </div>
  `,
})
export class VoyagerToastHostComponent {
  readonly service = inject(VoyagerToastService);

  readonly toasts = computed(() => this.service.toasts());
  readonly position = computed(() => this.service.position());

  iconFor(t: Toast): string { return VARIANT_ICONS[t.variant]; }

  titleFor(t: Toast): string {
    if (t.title) return t.title;
    // Fall through to the variant default; null neutral title doesn't
    // render anything so callers can keep raw "message only" toasts.
    const fallbacks: Record<ToastVariant, string> = {
      success: 'Done', error: 'Something went wrong', warning: 'Heads up',
      info: 'Note', loading: 'Working…', offline: 'Connection lost', neutral: '',
    };
    return fallbacks[t.variant];
  }

  invokeAction(a: ToastAction, id: number): void {
    a.onClick?.();
    if (a.dismiss !== false) this.service.dismiss(id);
  }
}
